import { useEffect, useRef, useState } from 'react'
import { BrainCircuit } from 'lucide-react'
import { aiHealthService, AiHealthLevel } from '../services/aiHealthService'
import { useAuth } from '../contexts/AuthContext'
import { useVelosterHealth } from '../hooks/useVelosterHealth'
import VelosterHealthPanel from './admin/VelosterHealthPanel'
import { whenVisible } from '@/utils/whenVisible'

const POLL_INTERVAL = 5 * 60 * 1000 // 5 min

type Tone = 'red' | 'amber' | 'neutral'

const TONE_CLASSES: Record<Tone, {
  badge: string
  dot: string
  popover: string
  iconBox: string
  icon: string
  title: string
  body: string
  link: string
}> = {
  red: {
    badge: 'bg-red-50 text-red-600 dark:bg-red-950/30 dark:text-red-400',
    dot: 'bg-red-500 dark:bg-red-400',
    popover: 'bg-red-50 dark:bg-red-950/90 border-red-300 dark:border-red-700',
    iconBox: 'bg-red-100 dark:bg-red-900/50 border-red-300 dark:border-red-700',
    icon: 'text-red-600 dark:text-red-400',
    title: 'text-red-900 dark:text-red-200',
    body: 'text-red-800 dark:text-red-300',
    link: 'text-red-700 dark:text-red-300 hover:text-red-900 dark:hover:text-red-100',
  },
  amber: {
    badge: 'bg-amber-50 text-amber-700 dark:bg-amber-950/30 dark:text-amber-400',
    dot: 'bg-amber-500 dark:bg-amber-400',
    popover: 'bg-amber-50 dark:bg-amber-950/90 border-amber-300 dark:border-amber-700',
    iconBox: 'bg-amber-100 dark:bg-amber-900/50 border-amber-300 dark:border-amber-700',
    icon: 'text-amber-600 dark:text-amber-400',
    title: 'text-amber-900 dark:text-amber-200',
    body: 'text-amber-800 dark:text-amber-300',
    link: 'text-amber-700 dark:text-amber-300 hover:text-amber-900 dark:hover:text-amber-100',
  },
  neutral: {
    badge: 'bg-zinc-100 text-zinc-700 dark:bg-zinc-900 dark:text-zinc-300',
    dot: 'bg-green-500 dark:bg-green-400',
    popover: 'bg-surface dark:bg-zinc-950 border-zinc-200 dark:border-zinc-800',
    iconBox: 'bg-zinc-100 dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800',
    icon: 'text-zinc-600 dark:text-zinc-400',
    title: 'text-zinc-900 dark:text-zinc-100',
    body: 'text-zinc-700 dark:text-zinc-400',
    link: 'text-zinc-700 dark:text-zinc-300 hover:text-zinc-900 dark:hover:text-zinc-100',
  },
}

export default function GeminiStatusBanner() {
  const { hasCap } = useAuth()
  const isDev = hasCap('can_use_dev_tools')

  const [status, setStatus] = useState<AiHealthLevel | null>(null)
  const veloster = useVelosterHealth(isDev)
  const [open, setOpen] = useState(false)
  const wrapperRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const check = async () => {
      try {
        const data = await aiHealthService.getStatus()
        setStatus(data.status)
      } catch {
        // silently ignore
      }
    }

    check()
    const id = setInterval(whenVisible(check), POLL_INTERVAL)
    return () => clearInterval(id)
  }, [])

  useEffect(() => {
    if (!open) return
    const onClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const isProblem = !!status && status !== 'operational'
  const isOutage = status === 'outage'

  // Não-dev: mantém o comportamento atual (só aparece se houver problema).
  // Dev/admin: badge sempre visível, pra permitir inspecionar as GPUs.
  if (!isDev && !isProblem) return null
  if (isDev && !status) return null // aguarda 1ª checagem antes de renderizar

  const tone: Tone = isOutage ? 'red' : isProblem ? 'amber' : 'neutral'
  const classes = TONE_CLASSES[tone]

  const label = isOutage ? 'Indisponível' : isProblem ? 'Instável' : 'Operacional'
  const geminiTitle = isOutage
    ? 'Serviço de IA fora do ar.'
    : isProblem
      ? 'Instabilidade no serviço de IA.'
      : 'Gemini operacional.'
  const geminiBody = isOutage
    ? 'A Comparação de Docs está indisponível no momento. Documentos já enviados serão processados assim que o serviço normalizar.'
    : isProblem
      ? 'A Comparação de Docs pode estar mais lenta ou com falhas no momento. Documentos já enviados serão processados assim que o serviço normalizar.'
      : 'Nenhuma interrupção detectada no momento.'

  return (
    <div
      ref={wrapperRef}
      // Wrapper só agrupa o hover; não é interativo em si — o botão dentro
      // dele já cobre clique/teclado (mesmo padrão de Tooltip.tsx).
      role="presentation"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
      className="relative flex items-center gap-1.5 text-xs border-r border-border-subtle dark:border-zinc-800 pr-4 mr-4"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="dialog"
        className={`flex items-center gap-1.5 px-2 py-1 rounded transition-opacity hover:opacity-80 ${classes.badge}`}
      >
        <BrainCircuit className="w-4 h-4" />
        <span className="font-semibold">IA</span>
        <span className={`w-2 h-2 rounded-full inline-block ${classes.dot}`} />
        <span className="text-xs font-medium">{label}</span>
      </button>

      {open && (
        <div
          role="dialog"
          className={`absolute top-full right-0 mt-2 rounded-lg border shadow-lg p-4 z-50 ${isDev ? 'w-96' : 'w-80'} ${classes.popover}`}
        >
          <div className="flex items-start gap-3">
            <div className={`flex-shrink-0 w-8 h-8 rounded-md border flex items-center justify-center ${classes.iconBox}`}>
              <BrainCircuit className={`w-4 h-4 ${classes.icon}`} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[11px] font-bold uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
                Gemini
              </p>
              <p className={`text-sm font-bold leading-snug ${classes.title}`}>
                {geminiTitle}
              </p>
              <p className={`text-sm leading-snug mt-1 ${classes.body}`}>
                {geminiBody}
              </p>
              {isProblem && (
                <a
                  href="https://status.cloud.google.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`inline-block mt-3 text-sm font-medium underline underline-offset-2 ${classes.link}`}
                >
                  Ver status do Google Cloud ↗
                </a>
              )}
            </div>
          </div>

          {isDev && (
            <div className="mt-4 pt-3 border-t border-zinc-200 dark:border-zinc-700">
              <p className="text-[11px] font-bold uppercase tracking-wide text-zinc-500 dark:text-zinc-400 mb-2">
                Veloster (local)
              </p>
              <VelosterHealthPanel veloster={veloster} />
            </div>
          )}
        </div>
      )}
    </div>
  )
}
